import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Edit, Trash2 } from "lucide-react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Icons } from "../../../components/ui/icons";
import SelectInput from "../../../components/select-input";
import SearchInput from "../../../components/search-input";
import DataTable, { TableRowData } from "../../../components/data-table";
import ShopModals from "./ShopModals";
import {
  formatShopMoney,
  SHOP_ORDER_STATUS_OPTIONS,
  SHOP_STATUS_OPTIONS,
} from "./constants";
import type { MobileAppTab } from "../constants";
import { isShopTab } from "../constants";
import {
  shopCategoriesTableColumns,
  shopOrdersTableColumns,
  shopProductsTableColumns,
} from "../../../lib/table-columns";
import {
  Product,
  ProductCategory,
  ShopRecordStatus,
  ProductOrderStatus,
  createProduct,
  createProductCategory,
  deleteProduct,
  deleteProductCategory,
  getProductCategories,
  getProductOrders,
  getProducts,
  removeProductImage,
  removeProductVideo,
  updateProduct,
  updateProductCategory,
  uploadProductImage,
  uploadProductVideo,
} from "../../../lib/api";
import { getBackendErrorMessage } from "../../../lib/utils";

export type ShopSectionHandle = {
  openCreateCategory: () => void;
  openCreateProduct: () => void;
};

interface ShopSectionProps {
  activeTab: MobileAppTab;
}

const ShopSection = forwardRef<ShopSectionHandle, ShopSectionProps>(function ShopSection(
  { activeTab },
  ref,
) {
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState("");
  const [categorySortOrder, setCategorySortOrder] = useState("");
  const [categoryStatus, setCategoryStatus] = useState<ShopRecordStatus>("ACTIVE");
  const [savingCategory, setSavingCategory] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);
  const [deletingCategory, setDeletingCategory] = useState(false);

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [productCategoryId, setProductCategoryId] = useState("");
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [productName, setProductName] = useState("");
  const [productSummary, setProductSummary] = useState("");
  const [productDetail, setProductDetail] = useState("");
  const [productPurchasePrice, setProductPurchasePrice] = useState("");
  const [productOfferPrice, setProductOfferPrice] = useState("");
  const [productStatus, setProductStatus] = useState<ShopRecordStatus>("ACTIVE");
  const [productImages, setProductImages] = useState<string[]>([]);
  const [productVideos, setProductVideos] = useState<string[]>([]);
  const [savingProduct, setSavingProduct] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const productImageInputRef = useRef<HTMLInputElement>(null);
  const productVideoInputRef = useRef<HTMLInputElement>(null);

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [togglingCategoryIds, setTogglingCategoryIds] = useState<Set<string>>(new Set());
  const [togglingProductIds, setTogglingProductIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, categoryFilter, orderStatusFilter, activeTab]);

  const categoryStatusParam =
    statusFilter === "all" ? undefined : (statusFilter as ShopRecordStatus);
  const productStatusParam =
    statusFilter === "all" ? undefined : (statusFilter as ShopRecordStatus);
  const orderStatusParam =
    orderStatusFilter === "all" ? undefined : (orderStatusFilter as ProductOrderStatus);

  const categoriesQuery = useQuery({
    queryKey: [
      "shop",
      "categories",
      { page: currentPage, limit: pageSize, search: searchTerm, status: categoryStatusParam },
    ],
    queryFn: async () =>
      getProductCategories(currentPage, pageSize, searchTerm, categoryStatusParam),
    enabled: activeTab === "Categories",
    placeholderData: keepPreviousData,
  });

  const allCategoriesQuery = useQuery({
    queryKey: ["shop", "categories", "all"],
    queryFn: async () => getProductCategories(1, 200, "", undefined),
    enabled: activeTab === "Products" || isProductModalOpen,
  });

  const productsQuery = useQuery({
    queryKey: [
      "shop",
      "products",
      {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        categoryId: categoryFilter === "all" ? undefined : categoryFilter,
        status: productStatusParam,
      },
    ],
    queryFn: async () =>
      getProducts(
        currentPage,
        pageSize,
        searchTerm,
        categoryFilter === "all" ? undefined : categoryFilter,
        productStatusParam,
      ),
    enabled: activeTab === "Products",
    placeholderData: keepPreviousData,
  });

  const ordersQuery = useQuery({
    queryKey: [
      "shop",
      "orders",
      {
        page: currentPage,
        limit: pageSize,
        search: searchTerm,
        status: orderStatusParam,
      },
    ],
    queryFn: async () =>
      getProductOrders(currentPage, pageSize, searchTerm, {
        status: orderStatusParam,
      }),
    enabled: activeTab === "Orders",
    placeholderData: keepPreviousData,
  });

  const categoryRows = useMemo(() => {
    const data = categoriesQuery.data?.data ?? [];
    return data.map((c: ProductCategory) => ({
      id: c.id,
      name: c.name,
      sort_order: c.sortOrder != null ? String(c.sortOrder) : "—",
      enabled: c.status === "ACTIVE",
      created_at: format(new Date(c.createdAt), "MMM dd, yyyy"),
      raw_data: c,
    })) as TableRowData[];
  }, [categoriesQuery.data]);

  const productRows = useMemo(() => {
    const data = productsQuery.data?.data ?? [];
    return data.map((p: Product) => ({
      id: p.id,
      name: p.name,
      category: p.category?.name ?? "—",
      purchase_price: formatShopMoney(p.purchasePrice),
      offer_price: formatShopMoney(p.offerPrice),
      image_count: String(p.images?.length ?? 0),
      video_count: String(p.videos?.length ?? 0),
      enabled: p.status === "ACTIVE",
      raw_data: p,
    })) as TableRowData[];
  }, [productsQuery.data]);

  const orderRows = useMemo(() => {
    const data = ordersQuery.data?.data ?? [];
    return data.map((o) => ({
      id: o.id,
      reference: o.reference,
      customer: o.user?.fullName ?? o.user?.email ?? "—",
      location: o.location || "—",
      item_count: String(o.items?.length ?? 0),
      attachment_count: String(o.attachments?.length ?? 0),
      status: o.status,
      created_at: format(new Date(o.createdAt), "MMM dd, yyyy HH:mm"),
      raw_data: o,
    })) as TableRowData[];
  }, [ordersQuery.data]);

  const categorySelectOptions = useMemo(
    () => [
      { value: "all", label: "All categories" },
      ...(allCategoriesQuery.data?.data ?? []).map((c) => ({
        value: c.id,
        label: c.name,
      })),
    ],
    [allCategoriesQuery.data],
  );

  const productCategoryOptions = useMemo(
    () =>
      (allCategoriesQuery.data?.data ?? [])
        .filter((c) => c.status === "ACTIVE")
        .map((c) => ({ value: c.id, label: c.name })),
    [allCategoriesQuery.data],
  );

  const categoriesTableLoading =
    activeTab === "Categories" &&
    categoriesQuery.isFetching &&
    !categoriesQuery.isLoading;
  const productsTableLoading =
    activeTab === "Products" &&
    productsQuery.isFetching &&
    !productsQuery.isLoading;
  const ordersTableLoading =
    activeTab === "Orders" &&
    ordersQuery.isFetching &&
    !ordersQuery.isLoading;

  const totalItems =
    activeTab === "Categories"
      ? (categoriesQuery.data?.meta.total ?? 0)
      : activeTab === "Products"
        ? (productsQuery.data?.meta.total ?? 0)
        : (ordersQuery.data?.meta.total ?? 0);

  const totalPages =
    activeTab === "Categories"
      ? (categoriesQuery.data?.meta.totalPages ?? 1)
      : activeTab === "Products"
        ? (productsQuery.data?.meta.totalPages ?? 1)
        : (ordersQuery.data?.meta.totalPages ?? 1);


  const resetCategoryForm = () => {
    setEditingCategoryId(null);
    setCategoryName("");
    setCategorySortOrder("");
    setCategoryStatus("ACTIVE");
  };

  const openCreateCategory = () => {
    resetCategoryForm();
    setIsCategoryModalOpen(true);
  };

  const openEditCategory = (raw: ProductCategory) => {
    setEditingCategoryId(raw.id);
    setCategoryName(raw.name);
    setCategorySortOrder(raw.sortOrder != null ? String(raw.sortOrder) : "");
    setCategoryStatus(raw.status);
    setIsCategoryModalOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return toast.error("Category name is required");

    const sort = categorySortOrder.trim() ? Number(categorySortOrder) : undefined;
    if (categorySortOrder.trim() && (!Number.isFinite(sort) || sort! < 0 || !Number.isInteger(sort))) {
      return toast.error("Sort order must be a whole number of 0 or greater");
    }

    setSavingCategory(true);
    try {
      if (editingCategoryId) {
        await updateProductCategory(editingCategoryId, {
          name: categoryName.trim(),
          status: categoryStatus,
          sortOrder: sort ?? null,
        });
        toast.success("Category updated");
      } else {
        await createProductCategory({
          name: categoryName.trim(),
          status: categoryStatus,
          sortOrder: sort,
        });
        toast.success("Category created");
      }
      setIsCategoryModalOpen(false);
      resetCategoryForm();
      await queryClient.invalidateQueries({ queryKey: ["shop", "categories"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save category"));
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategoryId) return;
    setDeletingCategory(true);
    try {
      await deleteProductCategory(deletingCategoryId);
      toast.success("Category deleted");
      setDeletingCategoryId(null);
      await queryClient.invalidateQueries({ queryKey: ["shop", "categories"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to delete category"));
    } finally {
      setDeletingCategory(false);
    }
  };

  const handleToggleCategoryStatus = async (row: TableRowData, enabled: boolean) => {
    const id = row.id as string;
    setTogglingCategoryIds((prev) => new Set(prev).add(id));
    try {
      await updateProductCategory(id, { status: enabled ? "ACTIVE" : "DISABLED" });
      toast.success(enabled ? "Category enabled" : "Category disabled");
      await queryClient.invalidateQueries({ queryKey: ["shop", "categories"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to update category status"));
    } finally {
      setTogglingCategoryIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleToggleProductStatus = async (row: TableRowData, enabled: boolean) => {
    const id = row.id as string;
    setTogglingProductIds((prev) => new Set(prev).add(id));
    try {
      await updateProduct(id, { status: enabled ? "ACTIVE" : "DISABLED" });
      toast.success(enabled ? "Product enabled" : "Product disabled");
      await queryClient.invalidateQueries({ queryKey: ["shop", "products"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to update product status"));
    } finally {
      setTogglingProductIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const resetProductForm = () => {
    setEditingProductId(null);
    setProductCategoryId("");
    setProductName("");
    setProductSummary("");
    setProductDetail("");
    setProductPurchasePrice("");
    setProductOfferPrice("");
    setProductStatus("ACTIVE");
    setProductImages([]);
    setProductVideos([]);
  };

  const openCreateProduct = () => {
    resetProductForm();
    setIsProductModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
    openCreateCategory,
    openCreateProduct,
  }));

  const openEditProduct = (raw: Product) => {
    setEditingProductId(raw.id);
    setProductCategoryId(raw.categoryId);
    setProductName(raw.name);
    setProductSummary(raw.summary ?? "");
    setProductDetail(raw.detail ?? "");
    setProductPurchasePrice(String(raw.purchasePrice));
    setProductOfferPrice(String(raw.offerPrice));
    setProductStatus(raw.status);
    setProductImages(raw.images ?? []);
    setProductVideos(raw.videos ?? []);
    setIsProductModalOpen(true);
  };

  const handleSaveProduct = async () => {
    if (!productCategoryId) return toast.error("Please select a category");
    if (!productName.trim()) return toast.error("Product name is required");
    const purchasePrice = Number(productPurchasePrice);
    const offerPrice = Number(productOfferPrice);
    if (!Number.isFinite(purchasePrice) || purchasePrice < 0) {
      return toast.error("Purchase price must be a valid number");
    }
    if (!Number.isFinite(offerPrice) || offerPrice < 0) {
      return toast.error("Offer price must be a valid number");
    }

    setSavingProduct(true);
    try {
      if (editingProductId) {
        await updateProduct(editingProductId, {
          categoryId: productCategoryId,
          name: productName.trim(),
          summary: productSummary.trim() || undefined,
          detail: productDetail.trim() || undefined,
          purchasePrice,
          offerPrice,
          status: productStatus,
        });
        toast.success("Product updated");
      } else {
        await createProduct({
          categoryId: productCategoryId,
          name: productName.trim(),
          summary: productSummary.trim() || undefined,
          detail: productDetail.trim() || undefined,
          purchasePrice,
          offerPrice,
          status: productStatus,
        });
        toast.success("Product created");
      }
      setIsProductModalOpen(false);
      resetProductForm();
      await queryClient.invalidateQueries({ queryKey: ["shop", "products"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save product"));
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!deletingProductId) return;
    setDeletingProduct(true);
    try {
      await deleteProduct(deletingProductId);
      toast.success("Product deleted");
      setDeletingProductId(null);
      await queryClient.invalidateQueries({ queryKey: ["shop", "products"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to delete product"));
    } finally {
      setDeletingProduct(false);
    }
  };

  const handleProductImageSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingProductId) return;
    setUploadingImage(true);
    try {
      const updated = await uploadProductImage(editingProductId, file);
      setProductImages(updated.images ?? []);
      setProductVideos(updated.videos ?? []);
      toast.success("Image uploaded");
      await queryClient.invalidateQueries({ queryKey: ["shop", "products"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to upload image"));
    } finally {
      setUploadingImage(false);
      if (productImageInputRef.current) productImageInputRef.current.value = "";
    }
  };

  const handleRemoveProductImage = async (image: string) => {
    if (!editingProductId) return;
    setUploadingImage(true);
    try {
      const updated = await removeProductImage(editingProductId, image);
      setProductImages(updated.images ?? []);
      setProductVideos(updated.videos ?? []);
      toast.success("Image removed");
      await queryClient.invalidateQueries({ queryKey: ["shop", "products"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to remove image"));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleProductVideoSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingProductId) return;
    setUploadingVideo(true);
    try {
      const updated = await uploadProductVideo(editingProductId, file);
      setProductImages(updated.images ?? []);
      setProductVideos(updated.videos ?? []);
      toast.success("Video uploaded");
      await queryClient.invalidateQueries({ queryKey: ["shop", "products"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to upload video"));
    } finally {
      setUploadingVideo(false);
      if (productVideoInputRef.current) productVideoInputRef.current.value = "";
    }
  };

  const handleRemoveProductVideo = async (video: string) => {
    if (!editingProductId) return;
    setUploadingVideo(true);
    try {
      const updated = await removeProductVideo(editingProductId, video);
      setProductImages(updated.images ?? []);
      setProductVideos(updated.videos ?? []);
      toast.success("Video removed");
      await queryClient.invalidateQueries({ queryKey: ["shop", "products"] });
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to remove video"));
    } finally {
      setUploadingVideo(false);
    }
  };

  const categoryActions = [
    {
      label: "Edit category",
      icon: Edit,
      onClick: (row: TableRowData) => openEditCategory((row as any).raw_data),
    },
    {
      label: "Delete category",
      icon: Trash2,
      onClick: (row: TableRowData) => setDeletingCategoryId(row.id as string),
    },
  ];

  const productActions = [
    {
      label: "Edit product",
      icon: Edit,
      onClick: (row: TableRowData) => openEditProduct((row as any).raw_data),
    },
    {
      label: "Delete product",
      icon: Trash2,
      onClick: (row: TableRowData) => setDeletingProductId(row.id as string),
    },
  ];

  const orderActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => setSelectedOrderId(row.id as string),
    },
  ];

  const closeCategoryModal = () => {
    setIsCategoryModalOpen(false);
    resetCategoryForm();
  };

  const closeProductModal = () => {
    setIsProductModalOpen(false);
    resetProductForm();
  };

  return (
    <>
      {isShopTab(activeTab) && (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2 w-full">
          <SearchInput
            searchTerm={searchInput}
            onSearchChange={setSearchInput}
            placeholder={
              activeTab === "Orders"
                ? "Search reference, location, customer..."
                : "Search..."
            }
          />
          {activeTab !== "Orders" && (
            <SelectInput
              options={SHOP_STATUS_OPTIONS}
              value={statusFilter}
              onChange={setStatusFilter}
              className="ml-auto"
            />
          )}
          {activeTab === "Products" && (
            <SelectInput
              options={categorySelectOptions}
              value={categoryFilter}
              onChange={setCategoryFilter}
            />
          )}
          {activeTab === "Orders" && (
            <SelectInput
              options={SHOP_ORDER_STATUS_OPTIONS}
              value={orderStatusFilter}
              onChange={setOrderStatusFilter}
              className="ml-auto"
            />
          )}
        </div>

        {activeTab === "Categories" && (
          <DataTable
            columns={shopCategoriesTableColumns}
            data={categoryRows}
            actions={categoryActions}
            loading={categoriesQuery.isLoading || categoriesTableLoading}
            enabledToggle={{
              onToggle: handleToggleCategoryStatus,
              isToggling: (rowId) => togglingCategoryIds.has(rowId),
            }}
            pagination={{
              page: currentPage,
              pageSize,
              totalItems,
              totalPages,
              onPageChange: setCurrentPage,
              onPageSizeChange: setPageSize,
            }}
          />
        )}

        {activeTab === "Products" && (
          <DataTable
            columns={shopProductsTableColumns}
            data={productRows}
            actions={productActions}
            loading={productsQuery.isLoading || productsTableLoading}
            enabledToggle={{
              onToggle: handleToggleProductStatus,
              isToggling: (rowId) => togglingProductIds.has(rowId),
            }}
            pagination={{
              page: currentPage,
              pageSize,
              totalItems,
              totalPages,
              onPageChange: setCurrentPage,
              onPageSizeChange: setPageSize,
            }}
          />
        )}

        {activeTab === "Orders" && (
          <DataTable
            columns={shopOrdersTableColumns}
            data={orderRows}
            actions={orderActions}
            loading={ordersQuery.isLoading || ordersTableLoading}
            pagination={{
              page: currentPage,
              pageSize,
              totalItems,
              totalPages,
              onPageChange: setCurrentPage,
              onPageSizeChange: setPageSize,
            }}
          />
        )}
      </div>
      )}

      <ShopModals
        isCategoryModalOpen={isCategoryModalOpen}
        onCloseCategoryModal={closeCategoryModal}
        editingCategoryId={editingCategoryId}
        categoryName={categoryName}
        onCategoryNameChange={setCategoryName}
        categorySortOrder={categorySortOrder}
        onCategorySortOrderChange={setCategorySortOrder}
        categoryStatus={categoryStatus}
        onCategoryStatusChange={setCategoryStatus}
        savingCategory={savingCategory}
        onSaveCategory={handleSaveCategory}
        isProductModalOpen={isProductModalOpen}
        onCloseProductModal={closeProductModal}
        editingProductId={editingProductId}
        productCategoryId={productCategoryId}
        onProductCategoryIdChange={setProductCategoryId}
        productCategoryOptions={productCategoryOptions}
        productName={productName}
        onProductNameChange={setProductName}
        productSummary={productSummary}
        onProductSummaryChange={setProductSummary}
        productDetail={productDetail}
        onProductDetailChange={setProductDetail}
        productPurchasePrice={productPurchasePrice}
        onProductPurchasePriceChange={setProductPurchasePrice}
        productOfferPrice={productOfferPrice}
        onProductOfferPriceChange={setProductOfferPrice}
        productStatus={productStatus}
        onProductStatusChange={setProductStatus}
        savingProduct={savingProduct}
        onSaveProduct={handleSaveProduct}
        productImages={productImages}
        productVideos={productVideos}
        uploadingImage={uploadingImage}
        uploadingVideo={uploadingVideo}
        productImageInputRef={productImageInputRef}
        productVideoInputRef={productVideoInputRef}
        onProductImageSelect={(e) => void handleProductImageSelect(e)}
        onRemoveProductImage={(url) => void handleRemoveProductImage(url)}
        onProductVideoSelect={(e) => void handleProductVideoSelect(e)}
        onRemoveProductVideo={(url) => void handleRemoveProductVideo(url)}
        deletingCategoryId={deletingCategoryId}
        onCloseDeleteCategory={() => {
          if (!deletingCategory) setDeletingCategoryId(null);
        }}
        deletingCategory={deletingCategory}
        onDeleteCategory={handleDeleteCategory}
        deletingProductId={deletingProductId}
        onCloseDeleteProduct={() => {
          if (!deletingProduct) setDeletingProductId(null);
        }}
        deletingProduct={deletingProduct}
        onDeleteProduct={handleDeleteProduct}
        selectedOrderId={selectedOrderId}
        onCloseOrderDetails={() => setSelectedOrderId(null)}
      />
    </>
  );
});

export default ShopSection;
